import { useMemo, useState } from 'react';
import { Card, Stat } from '@/shared/ui/atoms';
import { BUSINESSES, EDGES, NODES, type Health, type NodeKind, type ServiceEdge, type ServiceNode } from '@/features/service-dashboard/service-map/mock-data';

const HEALTH_LABEL: Record<Health, string> = { healthy: '정상', degraded: '저하', down: '장애' };
const HEALTH_STROKE: Record<Health, string> = { healthy: '#34d399', degraded: '#fbbf24', down: '#fb7185' };
const HEALTH_GLOW: Record<Health, string> = {
  healthy: 'rgba(52,211,153,0.65)',
  degraded: 'rgba(251,191,36,0.65)',
  down: 'rgba(251,113,133,0.75)',
};
const HEALTH_TEXT: Record<Health, string> = { healthy: '#6ee7b7', degraded: '#fcd34d', down: '#fda4af' };
const KIND_LABEL: Record<NodeKind, string> = { frontend: 'FE', service: 'SVC', datastore: 'DB', external: 'EXT', link: '↗' };
const LINK_COLOR = '#a78bfa';
const LINK_GLOW = 'rgba(167,139,250,0.7)';
const HUB_GLOW = 'rgba(56,189,248,0.85)';

const HEALTH_ROW_SKIN: Record<Health, string> = {
  healthy: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  degraded: 'border-amber-200 bg-amber-50 text-amber-800',
  down: 'border-rose-200 bg-rose-50 text-rose-700',
};

const NODE_WIDTH = 200;
const NODE_HEIGHT = 56;
const COLUMN_GAP = 100;
const ROW_GAP = 76;
const PADDING = 32;
const PITCH = NODE_WIDTH + COLUMN_GAP;

interface Placed extends ServiceNode {
  x: number;
  y: number;
}

type Side = 'left' | 'right';

/**
 * 허브를 캔버스 중앙에 놓고 나머지는 (허브까지 최단 hop 수) × (좌/우)로 방사시키는 레이아웃.
 * hop은 방향을 무시한 BFS로 구한다 — 화살표 방향과 무관하게 "허브에서 몇 단계 떨어졌나"만 본다.
 * 좌/우는 허브의 직접 이웃일 때만 kind로 정하고(프론트=왼쪽, 나머지=오른쪽), 그 다음부터는 부모 노드의
 * 좌/우를 그대로 물려받는다 — 그래야 같은 가지가 반대편으로 넘어가 선이 캔버스를 가로지르지 않는다.
 * 허브와 연결되지 않은(다른 연결 성분) 노드는 본 그래프 아래에 별도 줄로 둔다.
 */
function hubLayoutOf(nodes: ServiceNode[], edges: ServiceEdge[], hubId: string): { placed: Placed[]; width: number; height: number } {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const neighbors = new Map<string, string[]>();
  const addNeighbor = (a: string, b: string) => neighbors.set(a, [...(neighbors.get(a) ?? []), b]);
  edges.forEach((edge) => {
    addNeighbor(edge.from, edge.to);
    addNeighbor(edge.to, edge.from);
  });

  const hop = new Map<string, number>([[hubId, 0]]);
  const parent = new Map<string, string>();
  const side = new Map<string, Side>();
  const queue = [hubId];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const next of neighbors.get(current) ?? []) {
      if (hop.has(next)) continue;
      hop.set(next, (hop.get(current) ?? 0) + 1);
      parent.set(next, current);
      side.set(next, current === hubId ? (byId.get(next)?.kind === 'frontend' ? 'left' : 'right') : (side.get(current) as Side));
      queue.push(next);
    }
  }

  const reached = nodes.filter((node) => hop.has(node.id));
  const leftover = nodes.filter((node) => !hop.has(node.id));
  const maxHop = Math.max(0, ...reached.map((node) => hop.get(node.id) as number));
  const leftHopMax = Math.max(0, ...reached.filter((node) => side.get(node.id) === 'left').map((node) => hop.get(node.id) as number));
  const hubX = PADDING + leftHopMax * PITCH;

  const xOf = (id: string): number => {
    if (id === hubId) return hubX;
    const h = hop.get(id) as number;
    return side.get(id) === 'left' ? hubX - h * PITCH : hubX + h * PITCH;
  };

  const yRaw = new Map<string, number>([[hubId, 0]]);
  for (let h = 1; h <= maxHop; h += 1) {
    for (const s of ['left', 'right'] as const) {
      const bucket = reached.filter((node) => hop.get(node.id) === h && side.get(node.id) === s);
      if (bucket.length === 0) continue;
      const ordered = [...bucket].sort((left, right) => {
        const leftKey = yRaw.get(parent.get(left.id) as string) ?? 0;
        const rightKey = yRaw.get(parent.get(right.id) as string) ?? 0;
        return leftKey - rightKey || left.id.localeCompare(right.id);
      });
      const start = -((ordered.length - 1) * ROW_GAP) / 2;
      ordered.forEach((node, at) => yRaw.set(node.id, start + at * ROW_GAP));
      // 부모 평균 위치 쪽으로 묶음 전체를 당겨서 가지가 위아래로 흩어지지 않게 한다.
      const parentMean = ordered.reduce((sum, node) => sum + (yRaw.get(parent.get(node.id) as string) ?? 0), 0) / ordered.length;
      const bucketMean = ordered.reduce((sum, node) => sum + (yRaw.get(node.id) as number), 0) / ordered.length;
      const shift = parentMean - bucketMean;
      ordered.forEach((node) => yRaw.set(node.id, (yRaw.get(node.id) as number) + shift));
    }
  }

  const minY = Math.min(0, ...reached.map((node) => yRaw.get(node.id) as number));
  const placed: Placed[] = reached.map((node) => ({ ...node, x: xOf(node.id), y: (yRaw.get(node.id) as number) - minY + PADDING }));

  if (leftover.length > 0) {
    const mainBottom = Math.max(...placed.map((node) => node.y)) + NODE_HEIGHT;
    const mainLeft = Math.min(...placed.map((node) => node.x));
    leftover.forEach((node, at) => {
      placed.push({ ...node, x: mainLeft + at * PITCH, y: mainBottom + PADDING * 1.5 + (at % 2) * (ROW_GAP * 0.6) });
    });
  }

  const width = Math.max(...placed.map((node) => node.x)) + NODE_WIDTH + PADDING;
  const height = Math.max(...placed.map((node) => node.y)) + NODE_HEIGHT + PADDING;
  return { placed, width, height };
}

/** 사업 전체의 worst health — 탭 점 색으로 "이 사업 어딘가 문제 있다"를 바로 알려준다. */
function worstHealthOf(businessId: string): Health {
  const nodes = NODES[businessId].filter((node) => node.kind !== 'link');
  if (nodes.some((node) => node.health === 'down')) return 'down';
  if (nodes.some((node) => node.health === 'degraded')) return 'degraded';
  return 'healthy';
}

export function ServiceMapView(): React.JSX.Element {
  const [tab, setTab] = useState<string>(BUSINESSES[0].id);
  const [selected, setSelected] = useState<string | null>(null);

  const business = BUSINESSES.find((item) => item.id === tab) ?? BUSINESSES[0];
  const layout = useMemo(() => hubLayoutOf(NODES[tab], EDGES[tab], business.hub), [tab, business.hub]);
  const edges = EDGES[tab];
  const position = new Map(layout.placed.map((node) => [node.id, node]));

  const rows = useMemo(() => layout.placed.filter((node) => node.kind !== 'link'), [layout]);
  const healthy = rows.filter((node) => node.health === 'healthy').length;
  const degraded = rows.filter((node) => node.health === 'degraded').length;
  const down = rows.filter((node) => node.health === 'down').length;

  const goTo = (node: ServiceNode) => {
    if (node.kind === 'link' && node.linkTo) {
      setTab(node.linkTo);
      setSelected(null);
    } else {
      setSelected(node.id);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[11px] text-faint">
        POC — 실제 연동은 Cloud Trace 서비스맵(트레이스 기반) 또는 Cloud Monitoring 메트릭 API가 소스가 된다. 지금은
        노드·엣지·헬스 값 모두 mock이다. 탭은 repo가 아니라 <strong className="text-muted">사업 단위</strong>로
        나눴고, 파란 테두리 노드가 그 사업의 중심(허브)이다. 보라색 노드는 다른 사업으로 나가는 연동 —
        누르면 그 탭으로 건너간다.
      </p>

      <div className="flex flex-wrap gap-1.5 rounded-lg border border-line bg-surface p-1.5">
        {BUSINESSES.map((item) => (
          <TabButton key={item.id} active={tab === item.id} onClick={() => setTab(item.id)} label={item.label} dot={worstHealthOf(item.id)} />
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="이 사업 노드" value={rows.length} hint={business.summary} />
        <Stat label="정상" value={healthy} hint="health = healthy" tone="good" />
        <Stat label="저하" value={degraded} hint="health = degraded" tone="warn" />
        <Stat label="장애" value={down} hint="health = down" tone={down > 0 ? 'critical' : undefined} />
      </div>

      <Card title={`${business.label} 서비스맵`} hint={business.summary}>
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-faint">
          <span className="flex items-center gap-1.5">
            <i className="inline-block size-[7px] rounded-full" style={{ background: '#38bdf8' }} /> 허브(중심)
          </span>
          <span className="flex items-center gap-1.5">
            <i className="inline-block size-[7px] rounded-full" style={{ background: HEALTH_STROKE.healthy }} /> 정상
          </span>
          <span className="flex items-center gap-1.5">
            <i className="inline-block size-[7px] rounded-full" style={{ background: HEALTH_STROKE.degraded }} /> 저하
          </span>
          <span className="flex items-center gap-1.5">
            <i className="inline-block size-[7px] rounded-full" style={{ background: HEALTH_STROKE.down }} /> 장애
          </span>
          <span className="flex items-center gap-1.5">
            <i className="inline-block size-[7px] rounded-full" style={{ background: LINK_COLOR }} /> 다른 사업(누르면 이동)
          </span>
        </div>

        <div className="svc-map-panel max-h-[64vh] overflow-auto rounded-lg border border-slate-800">
          <svg width={layout.width} height={layout.height} role="img" aria-label={`${business.label} 서비스맵`}>
            <defs>
              <marker id="svc-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M0,0 L8,4 L0,8 z" fill="#94a3b8" />
              </marker>
              <marker id="svc-arrow-ext" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M0,0 L8,4 L0,8 z" fill="#fbbf24" />
              </marker>
            </defs>

            {edges.map((edge) => {
              const from = position.get(edge.from);
              const to = position.get(edge.to);
              if (!from || !to) return null;
              // 허브 왼쪽 가지는 hop이 늘수록 x가 줄어든다 — "누가 더 왼쪽인가"로 붙는 변을 고르되
              // 화살표(marker)는 항상 실제 edge.to 쪽 끝에 남긴다.
              const fromIsLeftOfTo = from.x <= to.x;
              const x1 = fromIsLeftOfTo ? from.x + NODE_WIDTH : from.x;
              const y1 = from.y + NODE_HEIGHT / 2;
              const x2 = fromIsLeftOfTo ? to.x : to.x + NODE_WIDTH;
              const y2 = to.y + NODE_HEIGHT / 2;
              const mid = (x1 + x2) / 2;
              const midY = (y1 + y2) / 2;
              return (
                <g key={`${edge.from}->${edge.to}`}>
                  <path
                    d={`M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`}
                    fill="none"
                    strokeWidth={1.6}
                    className="svc-map-edge"
                    stroke={edge.external ? '#fbbf24' : '#64748b'}
                    strokeOpacity={edge.external ? 0.8 : 0.7}
                    markerEnd={`url(#${edge.external ? 'svc-arrow-ext' : 'svc-arrow'})`}
                  >
                    <title>{edge.label ? `${edge.from} → ${edge.to} (${edge.label})` : `${edge.from} → ${edge.to}`}</title>
                  </path>
                  {edge.label && (
                    <text x={mid} y={midY - 6} textAnchor="middle" className="fill-slate-400 text-[9.5px]">
                      {edge.label}
                    </text>
                  )}
                </g>
              );
            })}

            {layout.placed.map((node) => (
              <NodeCard key={node.id} node={node} isHub={node.id === business.hub} selected={selected === node.id} onClick={() => goTo(node)} />
            ))}
          </svg>
        </div>
      </Card>

      <Card title="서비스별 상태" hint={`p50/p99·에러율은 최근 5분 mock 값 · ${rows.length}개 표시 중 (다른 사업 링크 노드는 제외)`}>
        <div className="rounded-lg border border-line-soft">
          <table className="w-full table-fixed border-collapse">
            <colgroup>
              <col className="w-[220px]" />
              <col className="w-[92px]" />
              <col className="w-[80px]" />
              <col className="w-[100px]" />
              <col className="w-[100px]" />
              <col className="w-[90px]" />
              <col />
            </colgroup>
            <thead>
              <tr className="[&>th]:border-b [&>th]:border-line-soft [&>th]:bg-[#f0f3f6] [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:text-xs [&>th]:font-semibold [&>th]:text-muted">
                <th>서비스</th>
                <th>상태</th>
                <th>p50</th>
                <th>p99</th>
                <th>에러율</th>
                <th>가동률</th>
                <th>비고</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((node) => (
                <RowOf key={node.id} node={node} highlighted={selected === node.id} onClick={() => setSelected(node.id)} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function TabButton({ active, onClick, label, dot }: { active: boolean; onClick: () => void; label: string; dot: Health }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap ${
        active ? 'bg-accent-soft font-semibold text-accent' : 'text-muted hover:bg-line-soft'
      }`}
    >
      <i className="inline-block size-[6px] rounded-full" style={{ background: HEALTH_STROKE[dot] }} />
      {label}
    </button>
  );
}

function NodeCard({ node, isHub, selected, onClick }: { node: Placed; isHub: boolean; selected: boolean; onClick: () => void }): React.JSX.Element {
  const isLink = node.kind === 'link';
  const rx = node.kind === 'datastore' || node.kind === 'external' ? 28 : 8;
  const stroke = isLink ? LINK_COLOR : isHub ? '#38bdf8' : HEALTH_STROKE[node.health];
  const glow = isLink ? LINK_GLOW : isHub ? HUB_GLOW : HEALTH_GLOW[node.health];
  return (
    <g transform={`translate(${node.x},${node.y})`} onClick={onClick} className="cursor-pointer">
      <title>{isLink ? `${node.label} · ${node.note}\n클릭하면 해당 사업 탭으로 이동` : `${node.label} · ${HEALTH_LABEL[node.health]}\n${node.note}`}</title>
      {isHub && (
        <rect
          x={-6}
          y={-6}
          width={NODE_WIDTH + 12}
          height={NODE_HEIGHT + 12}
          rx={rx + 6}
          fill="none"
          stroke="#38bdf8"
          strokeOpacity={0.4}
          strokeWidth={1.5}
          strokeDasharray="3 4"
        />
      )}
      <rect
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        rx={rx}
        fill="#111827"
        fillOpacity={0.92}
        stroke={selected ? '#38bdf8' : stroke}
        strokeWidth={selected || isHub ? 2 : 1.4}
        strokeDasharray={node.kind === 'external' || isLink ? '4 3' : undefined}
        style={{ filter: `drop-shadow(0 0 7px ${glow})` }}
      />
      <text x={12} y={17} className="fill-slate-500 font-mono text-[9.5px] font-bold tracking-wide">
        {isHub ? '허브' : KIND_LABEL[node.kind]}
      </text>
      {isLink ? (
        <text x={NODE_WIDTH - 10} y={17} textAnchor="end" fill={LINK_COLOR} className="text-[9.5px] font-semibold">
          다른 사업
        </text>
      ) : (
        <text x={NODE_WIDTH - 10} y={17} textAnchor="end" fill={HEALTH_TEXT[node.health]} className="text-[9.5px] font-semibold">
          {HEALTH_LABEL[node.health]}
        </text>
      )}
      <text x={12} y={34} className="fill-slate-100 text-[12.5px] font-bold">
        {clip(node.label)}
      </text>
      <text x={12} y={48} className="fill-slate-400 text-[10px]">
        {clip(node.note, 30)}
      </text>
    </g>
  );
}

function RowOf({ node, highlighted, onClick }: { node: ServiceNode; highlighted: boolean; onClick: () => void }): React.JSX.Element {
  return (
    <tr
      onClick={onClick}
      className={`cursor-pointer border-b border-line-soft last:border-0 [&>td]:px-2.5 [&>td]:py-2 [&>td]:align-middle ${
        highlighted ? 'bg-accent-soft' : 'hover:bg-canvas'
      }`}
    >
      <td className="truncate text-[12.5px] font-semibold">{node.label}</td>
      <td>
        <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${HEALTH_ROW_SKIN[node.health]}`}>
          {HEALTH_LABEL[node.health]}
        </span>
      </td>
      <td className="font-mono text-[11.5px] text-muted">{node.p50Ms !== null ? `${node.p50Ms}ms` : '-'}</td>
      <td className="font-mono text-[11.5px] text-muted">{node.p99Ms !== null ? `${node.p99Ms}ms` : '-'}</td>
      <td className={`font-mono text-[11.5px] ${node.errorRatePct !== null && node.errorRatePct > 1 ? 'text-rose-700 font-semibold' : 'text-muted'}`}>
        {node.errorRatePct !== null ? `${node.errorRatePct}%` : '-'}
      </td>
      <td className="font-mono text-[11.5px] text-muted">{node.uptimePct !== null ? `${node.uptimePct}%` : '-'}</td>
      <td className="truncate text-[11.5px] text-faint">{node.note}</td>
    </tr>
  );
}

function clip(label: string, max = 22): string {
  return label.length > max ? `${label.slice(0, max)}…` : label;
}
